import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHomeSlice } from 'app/pages/Home/slice';
import {
  saveUserPreferences,
  getUserPreferences,
  removeUserPreferenceField
} from 'utils/helpers';
import { useClearMustWinState } from 'app/hooks/useClearMustWinState';

/**
 * Custom hook to reset all Roadmap filters back to the default view.
 *
 * This is the same "Clear All" behavior used by the ClearAllButton — it
 * preserves the current swimlanes/tier selection (lanesTiers), matching the
 * app's existing definition of "default" filters.
 *
 * Usage:
 * ```typescript
 * const clearAllFilters = useClearAllFilters();
 *
 * clearAllFilters();
 * ```
 *
 * @returns A function that resets all filters to default
 */
export function useClearAllFilters() {
  const { actions } = useHomeSlice();
  const dispatch = useDispatch();
  const clearMustWinState = useClearMustWinState();

  const clearAllFilters = useCallback(() => {
    const currentPreferences = getUserPreferences();
    const preservedLanesTiers = currentPreferences.lanesTiers || [];

    // Clear all Redux state EXCEPT lanesTiers (swimlanes and tier filter)
    dispatch(actions.setSelectedGroups([]));
    dispatch(actions.setSelectedAudiences([]));
    dispatch(actions.setSelectedCats([]));
    dispatch(actions.setSelectedEvents([]));
    dispatch(actions.setSelectedFeatures([]));
    dispatch(actions.setSelectedProducts([]));
    dispatch(actions.setSelectedStatus([]));
    // DO NOT clear lanesTiers: dispatch(actions.setSelectedLanesTiers([]));
    dispatch(actions.clearHighlightedIssues());
    dispatch(actions.clearGenAiFilters());
    dispatch(actions.clearRevenueImpactFilters());
    dispatch(actions.clearMomentTypeFilter());
    clearMustWinState(); // Clear Must Win dropdown special UI

    dispatch(actions.setUnNestAll(false));
    dispatch(actions.clearPreviousUnNestAllState());
    dispatch(actions.clearCategoryProductSelection());

    // Clear filters in localStorage EXCEPT lanesTiers (swimlanes and tier filter)
    saveUserPreferences({
      productGroups: [],
      categories: [],
      events: [],
      productGroupLabels: [],
      eventsLabel: [],
      features: [],
      products: [],
      audiences: [],
      audienceLabels: [],
      status: [],
      lanesTiers: preservedLanesTiers,
      categoryProductCategories: [],
      categoryProductProducts: [],
      genAiFilters: [],
      revenueImpactFilters: [],
      momentTypeFilters: []
    });

    // Allows Roadmap.tsx to properly re-nest when filters are cleared
    removeUserPreferenceField('isUnNested');

    // Reload hierarchy filters with no selections to reset valid options
    dispatch(actions.loadHierarchyFilters({}));
  }, [actions, dispatch, clearMustWinState]);

  return clearAllFilters;
}
